import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useFormContext } from '@afosto/app-components-react/Form';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Checkbox from '@afosto/app-components-react/Checkbox';
import DateTimePicker from '@afosto/app-components-react/DateTimePicker';
import Select from '@afosto/app-components-react/Select';
import TextField from '@afosto/app-components-react/TextField';
import translations from './translations';

const DefaultFormSection = ({ pricingRule }) => {
  const intl = useIntl();
  const formContext = useFormContext();
  const formContextRef = useRef(formContext);
  const [typeSuggestions] = useState([
    { label: intl.formatMessage(translations.types.enhanced), value: 'enhanced' },
    { label: intl.formatMessage(translations.types.coupon), value: 'coupon' },
    { label: intl.formatMessage(translations.types.shippingCosts), value: 'shippingCosts' },
  ]);
  const [applyToSuggestions] = useState([
    {
      label: intl.formatMessage(translations.applyToOptions.linesHandling),
      value: 'lines_handling',
    },
    {
      label: intl.formatMessage(translations.applyToOptions.linesShipping),
      value: 'lines_shipping',
    },
    {
      label: intl.formatMessage(translations.applyToOptions.linesCheapest),
      value: 'lines_cheapest',
    },
    {
      label: intl.formatMessage(translations.applyToOptions.linesAll),
      value: 'lines_all',
    },
    {
      label: intl.formatMessage(translations.applyToOptions.filterResult),
      value: 'filter_result',
    },
    {
      label: intl.formatMessage(translations.applyToOptions.filterCheapest),
      value: 'filter_cheapest',
    },
  ]);
  const { watch } = formContext ?? {};
  const { id: pricingRuleID } = pricingRule ?? {};
  const typeValue = watch('type');

  useEffect(() => {
    formContextRef.current = formContext;
  });

  useEffect(() => {
    const setValue = formContextRef?.current?.setValue;

    const handleCoupon = () => {
      setValue('constraints', [
        {
          key: 'coupon',
          operator: 'in',
          values: [],
        },
      ]);

      setValue('filters', [
        {
          key: 'is_discounted',
          operator: 'in',
          values: [],
        },
      ]);
    };

    const handleShippingCosts = () => {
      setValue('constraints', [
        {
          key: 'amount',
          operator: 'gte',
          values: [],
        },
      ]);

      setValue('filters', [
        {
          key: 'is_discounted',
          operator: 'in',
          values: [],
        },
      ]);
    };

    if (!pricingRuleID && typeValue) {
      if (typeValue === 'coupon') {
        handleCoupon();
      }

      if (typeValue === 'shippingCosts') {
        handleShippingCosts();
      }
    }
  }, [pricingRuleID, typeValue]);

  return (
    <Card style={{ marginBottom: 8 }}>
      <CardContent>
        <TextField name="name" label={intl.formatMessage(translations.fields.name.label)} />

        <Select
          name="type"
          label={intl.formatMessage(translations.fields.type.label)}
          suggestions={typeSuggestions}
          readOnly={!!pricingRuleID}
          inline={!!pricingRuleID}
        />

        <Select
          name="applyTo"
          label={intl.formatMessage(translations.fields.applyTo.label)}
          suggestions={applyToSuggestions}
        />

        <TextField
          name="grantLimit"
          label={intl.formatMessage(translations.fields.grantLimit.label)}
          type="number"
          inputProps={{
            min: 0,
          }}
        />

        <TextField
          name="priority"
          label={intl.formatMessage(translations.fields.priority.label)}
          type="number"
          inputProps={{
            min: 0,
          }}
        />

        <DateTimePicker
          name="activeAt"
          label={intl.formatMessage(translations.fields.activeAt.label)}
          disableFuture={false}
        />

        <DateTimePicker
          name="expiresAt"
          label={intl.formatMessage(translations.fields.expiresAt.label)}
          disableFuture={false}
        />

        <Box mt={2}>
          <Checkbox
            name="isLastRule"
            label={intl.formatMessage(translations.fields.isLastRule.label)}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

DefaultFormSection.propTypes = {
  pricingRule: PropTypes.object,
};

DefaultFormSection.defaultProps = {
  pricingRule: {},
};

export default DefaultFormSection;
